/**
 * App-wide Mercure Subscriptions Configuration
 *
 * Each subscription defines:
 * - id:          Unique identifier for logging/debugging
 * - topic:       Topic pattern with :param placeholders (resolved at runtime)
 * - description: Human-readable description (dev tooling / logging)
 * - handler:     Async function receiving a deps object
 *
 * Handler deps: { client, userId, data, queryClient, toast, logout }
 *
 * Architecture note:
 * This file owns ALL real-time side-effects. Never put Mercure subscriptions
 * inside React hooks or components — AppMercureSubscriber wires everything here.
 */

import { queryKeys } from "@lib/api/queryKeys";
import type { NotificationRecord } from "@lib/queries/notificationQueries";
import type { QueryClient } from "@tanstack/react-query";

// ─── Shared types ──────────────────────────────────────────────────────────────

interface HandlerDeps {
  client: unknown;
  userId: string | number;
  data: Record<string, unknown>;
  queryClient: QueryClient;
  toast: {
    info: (title: string, options?: { description?: string; duration?: number }) => void;
    success: (title: string, options?: { description?: string; duration?: number }) => void;
    warning: (title: string, options?: { description?: string; duration?: number }) => void;
    error: (title: string, options?: { description?: string; duration?: number }) => void;
  };
  logout: () => void;
}

// ─── Notification settings helper ─────────────────────────────────────────────

interface NotificationUISettings {
  sound: boolean;
  duration: number;
  showToast: boolean;
}

const DEFAULT_NOTIFICATION_SETTINGS: NotificationUISettings = {
  sound: true,
  duration: 3000,
  showToast: true,
};

/**
 * Read notification UI preferences from the persisted Zustand store.
 * Safe to call outside React — reads localStorage directly.
 */
const getNotificationSettings = (): NotificationUISettings => {
  try {
    const stored = localStorage.getItem("neotim-ui");
    if (!stored) return DEFAULT_NOTIFICATION_SETTINGS;
    const parsed = JSON.parse(stored);
    return {
      ...DEFAULT_NOTIFICATION_SETTINGS,
      ...(parsed?.state?.settings?.notifications ?? {}),
    };
  } catch {
    return DEFAULT_NOTIFICATION_SETTINGS;
  }
};

// ─── Sound helper ──────────────────────────────────────────────────────────────

type SoundType = "info" | "success" | "warning" | "error";

const NOTIFICATION_SOUND_MAP: Record<string, SoundType> = {
  error: "error",
  critical: "error",
  warning: "warning",
  success: "success",
};

const playSound = async (type: string): Promise<void> => {
  const { playNotificationSound, isAudioSupported } = await import("@lib/utils/sound");
  if (!isAudioSupported()) return;
  const soundType: SoundType = NOTIFICATION_SOUND_MAP[type] ?? "info";
  playNotificationSound(soundType);
};

// ─── Toast helper ─────────────────────────────────────────────────────────────

const showNotificationToast = (
  notification: NotificationRecord,
  toast: HandlerDeps["toast"],
  duration: number,
): void => {
  if (!notification.title) return;

  const options = { description: notification.message ?? undefined, duration };

  switch (notification.type) {
    case "success":
      toast.success(notification.title, options);
      break;
    case "error":
    case "critical":
      toast.error(notification.title, options);
      break;
    case "warning":
      toast.warning(notification.title, options);
      break;
    default:
      toast.info(notification.title, options);
  }
};

// ─── Cache helpers ─────────────────────────────────────────────────────────────

/**
 * Prepend a new notification to every cached list that would include it.
 * Skips lists filtered by isRead=true (new notifications are always unread).
 * Deduplicates by id to be idempotent.
 */
const prependNotificationToLists = (
  notification: NotificationRecord,
  queryClient: QueryClient,
): void => {
  queryClient
    .getQueriesData<NotificationRecord[]>({ queryKey: queryKeys.notifications.lists() })
    .forEach(([queryKey, cachedData]) => {
      if (!Array.isArray(cachedData)) return;

      // Don't prepend to lists explicitly filtered to isRead=true
      const keyString = JSON.stringify(queryKey);
      if (keyString.includes('"isRead":true')) return;

      // Deduplicate
      if (cachedData.some((n) => n.id === notification.id)) return;

      queryClient.setQueryData<NotificationRecord[]>(queryKey, [
        notification,
        ...cachedData,
      ]);
    });
};

/**
 * Optimistically update a single notification across all cached lists.
 * Used when a notification is mutated in another session via Mercure.
 */
const updateNotificationInLists = (
  updated: Partial<NotificationRecord> & { id: number },
  queryClient: QueryClient,
): void => {
  queryClient
    .getQueriesData<NotificationRecord[]>({ queryKey: queryKeys.notifications.lists() })
    .forEach(([queryKey, cachedData]) => {
      if (!Array.isArray(cachedData)) return;
      queryClient.setQueryData<NotificationRecord[]>(
        queryKey,
        cachedData.map((n) => (n.id === updated.id ? { ...n, ...updated } : n)),
      );
    });
};

/**
 * Remove a notification from every cached list.
 * Used on soft-delete Mercure events.
 */
const removeNotificationFromLists = (id: number, queryClient: QueryClient): void => {
  queryClient
    .getQueriesData<NotificationRecord[]>({ queryKey: queryKeys.notifications.lists() })
    .forEach(([queryKey, cachedData]) => {
      if (!Array.isArray(cachedData)) return;
      queryClient.setQueryData<NotificationRecord[]>(
        queryKey,
        cachedData.filter((n) => n.id !== id),
      );
    });
};

// ─── Subscriptions ─────────────────────────────────────────────────────────────

export const APP_SUBSCRIPTIONS = [
  // ── User ──────────────────────────────────────────────────────────────────────
  {
    id: "user",
    topic: "/api/users/:userId",
    description: "User data changes (update, disable, password change)",
    handler: async ({ userId, data, queryClient, toast, logout }: HandlerDeps) => {
      switch (data?.type) {
        case "user.updated":
          queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
          toast.info("Vos informations ont été mises à jour.");
          break;

        case "user.disabled":
          toast.error("Votre compte a été désactivé. Déconnexion...");
          logout();
          break;

        case "user.password_changed":
          toast.info("Votre mot de passe a été modifié.");
          break;

        default:
          break;
      }
    },
  },

  // ── Permissions ───────────────────────────────────────────────────────────────
  {
    id: "permissions",
    topic: "/api/users/:userId/permissions",
    description: "Permission updates (granted, revoked, updated)",
    handler: async ({ userId, data, queryClient, toast, logout }: HandlerDeps) => {
      const invalidatePermissions = () =>
        queryClient.invalidateQueries({ queryKey: queryKeys.permissions.byUser(userId) });

      switch (data?.type) {
        case "permissions.updated":
          toast.info("Permissions mises à jour.");
          invalidatePermissions();
          break;

        case "permissions.granted":
          toast.success("De nouveaux accès vous ont été accordés.");
          invalidatePermissions();
          break;

        case "permissions.revoked":
          toast.error("Certains de vos accès ont été révoqués.");
          invalidatePermissions();
          break;

        case "user.disabled":
          toast.error("Votre compte a été désactivé. Déconnexion...");
          logout();
          break;

        default:
          break;
      }
    },
  },

  // ── Notifications ─────────────────────────────────────────────────────────────
  {
    id: "notifications",
    topic: "/api/notifications/:userId",
    description: "Real-time notification events (created, updated, deleted)",
    handler: async ({ data, queryClient, toast }: HandlerDeps) => {
      if (!data?.id) return;

      const notification = data as unknown as NotificationRecord;
      const settings = getNotificationSettings();

      // API Platform Mercure publishes the full serialized entity on mutation.
      // We disambiguate events by an explicit `eventType` field. If the backend
      // doesn't publish this field yet, we fall back to "notification.created"
      // so existing behaviour is preserved.
      const eventType =
        typeof data.eventType === "string" ? data.eventType : "notification.created";

      switch (eventType) {

        // ── New notification ────────────────────────────────────────────────────
        case "notification.created": {
          // 1. Optimistically prepend to relevant cached lists
          prependNotificationToLists(notification, queryClient);

          // 2. Invalidate the badge count — let the server be authoritative
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(),
          });

          // 3. Sound
          if (settings.sound) {
            await playSound(notification.type ?? "info");
          }

          // 4. Toast
          if (settings.showToast !== false) {
            showNotificationToast(notification, toast, settings.duration);
          }

          break;
        }

        // ── Read/unread toggled in another session ──────────────────────────────
        case "notification.updated": {
          updateNotificationInLists(
            {
              id: notification.id,
              isRead: notification.isRead,
              readAt: notification.readAt,
            },
            queryClient,
          );

          // Re-sync badge — we can't safely delta it without knowing the
          // previous isRead state across all cached lists
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(),
          });

          break;
        }

        // ── Soft-deleted ────────────────────────────────────────────────────────
        case "notification.deleted": {
          removeNotificationFromLists(notification.id, queryClient);

          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(),
          });

          break;
        }

        // ── Unknown event type ──────────────────────────────────────────────────
        default: {
          // Safe fallback: full invalidation rather than silently dropping
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.all,
          });
          break;
        }
      }
    },
  },
];

export default APP_SUBSCRIPTIONS;
